import type { LucideIcon } from "lucide-react";
import { MoreHorizontal } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";
import { Button } from "./button.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown.js";

/**
 * ItemCard (W-3 Vault): the stretched-link pattern. The item name is the
 * link (the caller's router Link or `<a>`, passed as `link`) and its
 * `::after` covers the whole card, so the card is one target. The row menu
 * is a sibling button ("More actions for {name}") above the stretched area.
 * A 1:1 photo with `role="img"`, or the category icon when there is none.
 * Badge, claim note, ValueAtRisk and CreditChip go in `children`.
 * Every string arrives translated; values arrive formatted.
 */

export interface ItemCardMenuItem {
  id: string;
  label: string;
  onSelect: () => void;
}

export interface ItemCardProps extends Omit<React.ComponentProps<"article">, "title"> {
  name: string;
  /** The link element; ItemCard renders the name inside it. */
  link: React.ReactElement<{ className?: string; children?: React.ReactNode }>;
  photoUrl?: string;
  /** Accessible name of the photo, e.g. "Photo of Rolex Submariner" (unused without `photoUrl`: the icon is decorative). */
  photoLabel: string;
  /** Shown when there is no photo. */
  fallbackIcon?: LucideIcon;
  /** Formatted value, e.g. "SGD $12,000.00". */
  value?: string;
  /** Accessible name of the menu button, e.g. "More actions for Rolex". */
  menuLabel: string;
  menuItems: ItemCardMenuItem[];
  /** Passed to the menu content (e.g. preventDefault to move focus yourself). */
  onMenuCloseAutoFocus?: (event: Event) => void;
  headingLevel?: 2 | 3 | 4;
}

function ItemCard({
  name,
  link,
  photoUrl,
  photoLabel,
  fallbackIcon: Icon,
  value,
  menuLabel,
  menuItems,
  onMenuCloseAutoFocus,
  headingLevel = 3,
  className,
  children,
  ...props
}: ItemCardProps) {
  const Heading = `h${headingLevel}` as "h3";
  const linked = React.cloneElement(
    link,
    {
      className: cn(
        "rounded-sm after:absolute after:inset-0 after:rounded-lg after:content-[''] hover:underline focus-visible:outline-2 focus-visible:outline-ring",
        link.props.className,
      ),
    },
    name,
  );
  return (
    <article
      data-slot="item-card"
      className={cn(
        "relative flex gap-3 rounded-lg border bg-card p-3 text-card-foreground hover:bg-muted/50 xl:flex-col",
        className,
      )}
      {...props}
    >
      <div
        {...(photoUrl ? { role: "img", "aria-label": photoLabel } : { "aria-hidden": true })}
        className="flex aspect-square w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted xl:w-full"
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="size-full object-cover" loading="lazy" />
        ) : Icon ? (
          <Icon aria-hidden="true" className="size-8 text-muted-foreground" />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <Heading className="min-w-0 break-words font-semibold">{linked}</Heading>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={menuLabel}
                className="relative z-10 -mt-1 -mr-1 size-11 shrink-0"
              >
                <MoreHorizontal aria-hidden="true" className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onCloseAutoFocus={onMenuCloseAutoFocus}>
              {menuItems.map((m) => (
                <DropdownMenuItem key={m.id} onSelect={m.onSelect} className="min-h-11">
                  {m.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {value ? <p className="font-semibold tabular-nums">{value}</p> : null}
        {children ? <div className="flex flex-col items-start gap-1.5">{children}</div> : null}
      </div>
    </article>
  );
}

export { ItemCard };
